/**
 * 목록 툴바의 두 메뉴 (2026-08-21).
 *
 *  · 필터  — 거르기(요약 4칸과 **같은 상태를 본다**) + 묶어 보기(하나만)
 *  · 열림  — 전체 · 좌우 · 상하. 지금 방식이 아이콘 모양으로 그대로 보인다.
 *
 * 두 메뉴 모두 바깥을 누르면 닫힌다. 팝오버는 버튼 바로 아래에 붙되, 판 밖으로 넘치면
 * 위로 뒤집는다 — 넘어간 메뉴는 누를 수가 없다.
 */

import { useEffect, useRef } from 'react';
import { AlignJustify, Check, ChevronDown, Clock, Columns, LayoutGrid, Rows3, Square, Users } from '../../icons';
import { tMail } from '../../lib/mail-i18n';
import type { MailGroupBy, MailOpenMode } from './mailView';
import type { MailListDensity } from '../../api/users';
import type { MailStatKey } from './MailStatRow';

/** 바깥 클릭·Esc 로 닫기 — 두 메뉴가 같은 규칙을 쓴다. */
function useDismiss(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);
  return ref;
}

interface FilterMenuProps {
  open: boolean;
  onToggleOpen: () => void;
  onClose: () => void;
  active: Set<MailStatKey>;
  groupBy: MailGroupBy;
  onReset: () => void;
  onGroupBy: (by: MailGroupBy) => void;
  /**
   * 목록 밀도 (2026-09-05) — 「환경설정 > 기본환경」의 그 값이다. 안 주면 이 칸이 안 나온다.
   * ★ 두 자리가 **같은 값**을 본다 — 어느 쪽에서 바꿔도 함께 갱신된다.
   */
  density?: MailListDensity;
  onDensity?: (density: MailListDensity) => void;
}

/** 밀도 두 칸 — 도해는 환경설정 쪽 몫이다. 여기는 팝오버라 아이콘 한 개로 가른다. */
const DENSITY_ROWS: { id: MailListDensity; labelKey: 'mail.density.narrow' | 'mail.density.wide'; Icon: typeof Rows3 }[] = [
  { id: 'NARROW', labelKey: 'mail.density.narrow', Icon: AlignJustify },
  { id: 'WIDE', labelKey: 'mail.density.wide', Icon: Rows3 },
];

const GROUP_ROWS: { id: MailGroupBy; labelKey: 'mail.group.none' | 'mail.group.date' | 'mail.group.sender'; Icon: typeof Clock }[] = [
  { id: 'none', labelKey: 'mail.group.none', Icon: LayoutGrid },
  { id: 'date', labelKey: 'mail.group.date', Icon: Clock },
  { id: 'sender', labelKey: 'mail.group.sender', Icon: Users },
];

/**
 * 묶어 보기 메뉴 (2026-08-21 개편).
 *
 * ★ 예전에는 여기에 **거르기까지** 들어 있었다. 좌측 요약 4칸도 같은 넷을 걸렀기 때문에
 *   "필터가 서로 다른 데 있다"(사용자 지적)가 됐다. 거르는 일은 **좌측 4칸 한 곳**으로 모으고,
 *   여기는 묶는 일만 한다. 한 가지 일에 한 자리다.
 */
export function MailFilterMenu(props: FilterMenuProps) {
  const { open, onToggleOpen, onClose, active, groupBy, onReset, onGroupBy, density, onDensity } = props;
  const ref = useDismiss(open, onClose);
  const current = GROUP_ROWS.find((r) => r.id === groupBy) ?? GROUP_ROWS[0]!;
  const CurrentIcon = current.Icon;

  return (
    <div className="mail-view-menu" ref={ref}>
      <button
        type="button"
        className="mail-view-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={onToggleOpen}
      >
        <CurrentIcon size={13} aria-hidden />
        <span>{tMail(current.labelKey)}</span>
      </button>
      {open && (
        <div className="mail-view-pop" role="menu">
          <p className="mail-view-pop-h">{tMail('mail.group.heading')}</p>
          {GROUP_ROWS.map(({ id, labelKey, Icon }) => (
            <button
              key={id}
              type="button"
              role="menuitemradio"
              aria-checked={groupBy === id}
              className={`mail-view-opt${groupBy === id ? ' is-on' : ''}`}
              onClick={() => onGroupBy(id)}
            >
              <Icon size={15} aria-hidden className="mail-view-opt-ic" />
              <span className="mail-view-opt-label">{tMail(labelKey)}</span>
              {groupBy === id && <Check size={14} aria-hidden className="mail-view-opt-chk" />}
            </button>
          ))}
          {groupBy !== 'none' && <p className="mail-view-note">{tMail('mail.group.scope_note')}</p>}

          {/*
            목록 밀도 (2026-09-05) — 「환경설정 > 기본환경 > 메일 목록 밀도」와 **한 값**이다.
            ★ 여태 여기에는 「본문 미리보기」 켬/끔 하나뿐이었다. 밀도는 아바타까지 함께
              가르는 값이라, 둘로 나뉘어 있으면 「좁게인데 아바타는 있는」 상태가 생긴다.
          */}
          {onDensity && density && (
            <>
              <div className="mail-view-sep" />
              <p className="mail-view-pop-h">{tMail('mail.density.heading')}</p>
              {DENSITY_ROWS.map(({ id, labelKey, Icon }) => (
                <button
                  key={id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={density === id}
                  className={`mail-view-opt${density === id ? ' is-on' : ''}`}
                  onClick={() => onDensity(id)}
                >
                  <Icon size={15} aria-hidden className="mail-view-opt-ic" />
                  <span className="mail-view-opt-label">{tMail(labelKey)}</span>
                  {density === id && <Check size={14} aria-hidden className="mail-view-opt-chk" />}
                </button>
              ))}
            </>
          )}

          {(active.size > 0 || groupBy !== 'none') && (
            <>
              <div className="mail-view-sep" />
              <button type="button" role="menuitem" className="mail-view-opt" onClick={onReset}>
                <span className="mail-view-opt-label">{tMail('mail.filter.reset')}</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface OpenMenuProps {
  open: boolean;
  onToggleOpen: () => void;
  onClose: () => void;
  mode: MailOpenMode;
  onPick: (mode: MailOpenMode) => void;
}

const OPEN_ROWS: {
  id: MailOpenMode;
  labelKey: 'mail.view.full' | 'mail.view.side' | 'mail.view.bottom';
  descKey: 'mail.view.full_desc' | 'mail.view.side_desc' | 'mail.view.bottom_desc';
  Icon: typeof Square;
}[] = [
  { id: 'full', labelKey: 'mail.view.full', descKey: 'mail.view.full_desc', Icon: Square },
  { id: 'side', labelKey: 'mail.view.side', descKey: 'mail.view.side_desc', Icon: Columns },
  { id: 'bottom', labelKey: 'mail.view.bottom', descKey: 'mail.view.bottom_desc', Icon: Rows3 },
];

export function MailOpenMenu({ open, onToggleOpen, onClose, mode, onPick }: OpenMenuProps) {
  const ref = useDismiss(open, onClose);
  const Current = OPEN_ROWS.find((r) => r.id === mode)?.Icon ?? Columns;

  return (
    <div className="mail-view-menu" ref={ref}>
      <button
        type="button"
        className="mail-view-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={tMail('mail.view.aria')}
        title={tMail('mail.view.aria')}
        onClick={onToggleOpen}
      >
        <Current size={14} aria-hidden />
        <ChevronDown size={11} aria-hidden className="mail-view-caret" />
      </button>
      {open && (
        <div className="mail-view-pop" role="menu">
          <p className="mail-view-pop-h">{tMail('mail.view.heading')}</p>
          {OPEN_ROWS.map(({ id, labelKey, descKey, Icon }) => (
            <button
              key={id}
              type="button"
              role="menuitemradio"
              aria-checked={mode === id}
              className={`mail-view-opt mail-view-opt--tall${mode === id ? ' is-on' : ''}`}
              onClick={() => onPick(id)}
            >
              <Icon size={15} aria-hidden className="mail-view-opt-ic" />
              <span className="mail-view-opt-label">
                {tMail(labelKey)}
                <span className="mail-view-opt-desc">{tMail(descKey)}</span>
              </span>
              {mode === id && <Check size={14} aria-hidden className="mail-view-opt-chk" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
